/**
 * Standard-score scales and the conversions between them, all through z.
 *
 * Every linear scale is mean + SD × z:
 * - T-score: 50 + 10z (McCall, 1922).
 * - Deviation IQ: 100 + 15z (Wechsler scales, and the current Stanford–Binet).
 * - Normal curve equivalent (NCE): 50 + 21.06z, the SD chosen so NCE 1, 50 and 99 match
 *   percentile ranks 1, 50 and 99 (Tallmadge & Wood, 1976, ESEA Title I evaluation
 *   system).
 * - Scaled score (Wechsler subtests): 10 + 3z.
 * - College Board (CEEB) scale: 500 + 100z.
 * Two scales are banded rather than continuous:
 * - Stanine ("standard nine", developed for the US Army Air Forces in the 1940s):
 *   mean 5, SD 2, so stanine = round(2z + 5) limited to 1–9. Bands are half a standard
 *   deviation wide, centred on 0.
 * - Sten ("standard ten", R. B. Cattell): mean 5.5, SD 2, sten = round(2z + 5.5) limited
 *   to 1–10; band edges fall at whole and half standard deviations.
 * Percentile ranks assume a normal distribution: P = 100 × Φ(z).
 */

import { normalCdf, normalQuantile } from "@/lib/stats/distributions";

export type ScaleId =
  | "z"
  | "t"
  | "iq"
  | "nce"
  | "scaled"
  | "ceeb"
  | "percentile"
  | "stanine"
  | "sten"
  | "raw";

export interface LinearScale {
  id: "z" | "t" | "iq" | "nce" | "scaled" | "ceeb";
  mean: number;
  sd: number;
}

export const LINEAR_SCALES: readonly LinearScale[] = [
  { id: "z", mean: 0, sd: 1 },
  { id: "t", mean: 50, sd: 10 },
  { id: "iq", mean: 100, sd: 15 },
  { id: "nce", mean: 50, sd: 21.06 },
  { id: "scaled", mean: 10, sd: 3 },
  { id: "ceeb", mean: 500, sd: 100 },
];

export const STANINE_MIN = 1;
export const STANINE_MAX = 9;
export const STEN_MIN = 1;
export const STEN_MAX = 10;

export type ConversionError =
  | "notANumber"
  | "percentileRange"
  | "stanineRange"
  | "stenRange"
  | "sdNotPositive";

export interface ScoreInput {
  scale: ScaleId;
  value: number;
  /** Mean and SD of the raw-score distribution, for `scale: "raw"`. */
  mean?: number;
  sd?: number;
}

export interface StandardScores {
  z: number;
  /** Percentile rank, 0–100, under a normal distribution. */
  percentile: number;
  t: number;
  iq: number;
  nce: number;
  scaled: number;
  ceeb: number;
  stanine: number;
  sten: number;
}

export interface Band {
  /** Inclusive lower and exclusive upper z of the band (infinite at the ends). */
  lowZ: number;
  highZ: number;
  lowPercentile: number;
  highPercentile: number;
}

export interface Conversion {
  z: number;
  scores: StandardScores;
  /** A banded input (stanine, sten) stands for a whole band; z is the band's centre. */
  band: Band | null;
}

const ROUNDING_EPSILON = 1e-9;

function roundHalfUp(value: number): number {
  return Math.floor(value + 0.5 + ROUNDING_EPSILON);
}

export function stanineOf(z: number): number {
  return Math.min(STANINE_MAX, Math.max(STANINE_MIN, roundHalfUp(2 * z + 5)));
}

export function stenOf(z: number): number {
  return Math.min(STEN_MAX, Math.max(STEN_MIN, roundHalfUp(2 * z + 5.5)));
}

export function percentileOf(z: number): number {
  return 100 * normalCdf(z);
}

function bandFor(lowZ: number, highZ: number): Band {
  return {
    lowZ,
    highZ,
    lowPercentile: Number.isFinite(lowZ) ? percentileOf(lowZ) : 0,
    highPercentile: Number.isFinite(highZ) ? percentileOf(highZ) : 100,
  };
}

/** The z-range of one stanine: (stanine − 5.5) ÷ 2 up to (stanine − 4.5) ÷ 2. */
export function stanineBand(stanine: number): Band {
  return bandFor(
    stanine === STANINE_MIN ? Number.NEGATIVE_INFINITY : (stanine - 5.5) / 2,
    stanine === STANINE_MAX ? Number.POSITIVE_INFINITY : (stanine - 4.5) / 2
  );
}

/** The z-range of one sten: (sten − 6) ÷ 2 up to (sten − 5) ÷ 2. */
export function stenBand(sten: number): Band {
  return bandFor(
    sten === STEN_MIN ? Number.NEGATIVE_INFINITY : (sten - 6) / 2,
    sten === STEN_MAX ? Number.POSITIVE_INFINITY : (sten - 5) / 2
  );
}

export function scoresFromZ(z: number): StandardScores {
  const linear = (scale: LinearScale["id"]): number => {
    const found = LINEAR_SCALES.find((each) => each.id === scale);
    return (found?.mean ?? 0) + (found?.sd ?? 1) * z;
  };
  return {
    z,
    percentile: percentileOf(z),
    t: linear("t"),
    iq: linear("iq"),
    nce: linear("nce"),
    scaled: linear("scaled"),
    ceeb: linear("ceeb"),
    stanine: stanineOf(z),
    sten: stenOf(z),
  };
}

function linearToZ(input: ScoreInput): number | ConversionError {
  if (input.scale === "raw") {
    const sd = input.sd ?? Number.NaN;
    const mean = input.mean ?? Number.NaN;
    if (!(Number.isFinite(sd) && Number.isFinite(mean))) {
      return "notANumber";
    }
    return sd > 0 ? (input.value - mean) / sd : "sdNotPositive";
  }
  const scale = LINEAR_SCALES.find((each) => each.id === input.scale);
  return scale ? (input.value - scale.mean) / scale.sd : "notANumber";
}

function isWhole(value: number): boolean {
  return Number.isInteger(value);
}

function bandedScore(
  scale: "stanine" | "sten",
  value: number
): Conversion | ConversionError {
  const stanine = scale === "stanine";
  const min = stanine ? STANINE_MIN : STEN_MIN;
  const max = stanine ? STANINE_MAX : STEN_MAX;
  if (!isWhole(value) || value < min || value > max) {
    return stanine ? "stanineRange" : "stenRange";
  }
  const z = stanine ? (value - 5) / 2 : (value - 5.5) / 2;
  return {
    z,
    scores: scoresFromZ(z),
    band: stanine ? stanineBand(value) : stenBand(value),
  };
}

/** Convert one score on any supported scale to all the others. */
export function convertScore(input: ScoreInput): Conversion | ConversionError {
  const { scale, value } = input;
  if (!Number.isFinite(value)) {
    return "notANumber";
  }
  if (scale === "percentile") {
    if (value <= 0 || value >= 100) {
      return "percentileRange";
    }
    const z = normalQuantile(value / 100);
    return { z, scores: scoresFromZ(z), band: null };
  }
  if (scale === "stanine" || scale === "sten") {
    return bandedScore(scale, value);
  }
  const z = linearToZ(input);
  if (typeof z === "string") {
    return z;
  }
  return { z, scores: scoresFromZ(z), band: null };
}

export function isConversion(
  value: Conversion | ConversionError
): value is Conversion {
  return typeof value !== "string";
}

/** The nine stanines with their conventional share of a normal population. */
export function stanineTable(): { stanine: number; band: Band }[] {
  return Array.from({ length: STANINE_MAX }, (_, i) => ({
    stanine: i + 1,
    band: stanineBand(i + 1),
  }));
}
